import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, TrendingUp, Target, RefreshCw } from 'lucide-react';

const INTEREST_COLORS = { faible: '#6B7280', moyen: '#F59E0B', eleve: '#10B981', inconnu: '#9CA3AF' };

export default function AISummaryBanner({ prospectId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closingScore, setClosingScore] = useState(null);

  const loadScore = async () => {
    try {
      const { data } = await api.get(`/ai/closing-score/${prospectId}`);
      setClosingScore(data);
    } catch {}
  };

  const generateSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/summarize-prospect/${prospectId}`);
      setSummary(data);
    } catch (e) {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadScore(); }, [prospectId]); // eslint-disable-line

  const score = summary?.score_closing ?? closingScore?.score ?? 0;
  const scoreColor = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#6B7280';

  return (
    <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] border border-[#F97316]/20 rounded-lg p-3" data-testid="ai-summary-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-xs font-bold text-[#7C2D12] uppercase tracking-wider">Synthèse IA</h4>
            <div className="flex items-center gap-1 ml-auto">
              <div className="flex items-center gap-1 bg-white px-2 py-0.5 rounded-full border border-[#E5E7EB]">
                <Target className="w-3 h-3" style={{ color: scoreColor }} />
                <span className="text-[10px] font-bold" style={{ color: scoreColor }}>{score}%</span>
                <span className="text-[9px] text-[#71717A]">closing</span>
              </div>
            </div>
          </div>

          {!summary && !loading && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-[#7C2D12]">Cliquez pour obtenir une synthèse IA de ce prospect (situation, besoin, prochaine action).</p>
              <Button size="sm" onClick={generateSummary} className="bg-[#F97316] hover:bg-[#EA580C] h-7 text-xs" data-testid="generate-summary-btn">
                <Sparkles className="w-3 h-3 mr-1" />Analyser
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-xs text-[#7C2D12]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> L'IA analyse l'historique…
            </div>
          )}

          {summary && !loading && (
            <div className="space-y-1.5" data-testid="summary-content">
              <div className="text-xs"><strong className="text-[#7C2D12]">Situation :</strong> <span className="text-[#18181B]">{summary.situation}</span></div>
              <div className="text-xs"><strong className="text-[#7C2D12]">Besoin :</strong> <span className="text-[#18181B]">{summary.besoin}</span></div>
              <div className="text-xs flex items-start gap-1">
                <strong className="text-[#7C2D12]">Prochaine action :</strong>
                <span className="text-[#F97316] font-semibold">{summary.prochaine_action}</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Badge className="text-[10px] h-4" style={{ background: INTEREST_COLORS[summary.niveau_interet] || '#9CA3AF' }}>
                  Intérêt : {summary.niveau_interet}
                </Badge>
                <Button size="sm" variant="ghost" onClick={generateSummary} className="h-5 px-1.5 text-[10px] ml-auto" data-testid="regen-summary-btn">
                  <RefreshCw className="w-2.5 h-2.5 mr-1" />Regénérer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
